import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import type {
  BidUpgradeType,
  EntryUpgradeType,
  UpgradeType,
} from '@freelancer/components/upgrade-tag';
import { Currency } from '@freelancer/datastore/collections';
import { VerticalAlignment } from '@freelancer/ui/grid';
import { IconSize } from '@freelancer/ui/icon';

@Component({
  standalone: false,
  selector: 'fl-upgrade-item',
  template: `
    <fl-grid
      [vAlign]="
        variation === 'compact' ? VerticalAlignment.VERTICAL_CENTER : undefined
      "
    >
      @if (variation === 'default') {
        <fl-col
          class="mb-xxsmall"
          [col]="6"
          [colTablet]="3"
        >
          <ng-container [ngTemplateOutlet]="upgradeTag"></ng-container>
        </fl-col>
        <fl-col
          class="mb-xxsmall"
          [col]="6"
          [colTablet]="3"
          [colTabletOrder]="3"
        >
          <ng-container [ngTemplateOutlet]="upgradePrice"></ng-container>
        </fl-col>
        <fl-col
          [col]="12"
          [colTablet]="6"
        >
          <ng-container [ngTemplateOutlet]="description"></ng-container>
        </fl-col>
      }

      @if (variation === 'compact') {
        <fl-col
          [col]="12"
          [colTablet]="9"
        >
          <div class="UpgradeCompactHeader mb-xxsmall">
            <ng-container [ngTemplateOutlet]="upgradeTag"></ng-container>
            <div class="mobile:show">
              <ng-container [ngTemplateOutlet]="upgradePrice"></ng-container>
            </div>
          </div>
          <ng-container [ngTemplateOutlet]="description"></ng-container>
        </fl-col>
        <fl-col
          class="mobile:hide"
          [col]="3"
        >
          <ng-container [ngTemplateOutlet]="upgradePrice"></ng-container>
        </fl-col>
      }
    </fl-grid>

    <ng-template #upgradeTag>
      <div class="UpgradeItem">
        <fl-upgrade-tag
          class="UpgradeItem-tag"
          [attr.data-variation]="variation"
          [upgradeType]="upgradeType"
        ></fl-upgrade-tag>
      </div>
    </ng-template>

    <ng-template #description>
      <p class="desktop:mb-none">
        <ng-content></ng-content>
      </p>
    </ng-template>

    <ng-template #upgradePrice>
      @if (!isLoadingPrice) {
        <div class="UpgradePricingWrapper">
          <div class="UpgradePrice mr-xxsmall">
            @if (includedUpgrade && !isSoldOut) {
              @if (price) {
                <p class="UpgradeItem-price-full text-right font-bold">
                  {{ price | flCurrency: currency?.code }}
                </p>
              }
              <p
                class="text-right font-bold"
                i18n="Included tag for an upgrade item"
              >
                Included
              </p>
            }
            @if (!isSoldOut && !includedUpgrade) {
              @if (!upgradeApplied && !promoPrice && price && !freeUpgrade) {
                <p class="text-right font-bold">
                  {{ price | flCurrency: currency?.code }}
                </p>
              }
              @if (
                !upgradeApplied && ((!promoPrice && price === 0) || freeUpgrade)
              ) {
                <p
                  class="text-right uppercase font-bold"
                  i18n="Free tag for an upgrade item"
                >
                  Free
                </p>
              }
              @if (upgradeApplied && !promoPrice) {
                <p
                  class="text-right font-bold"
                  i18n="Project upgrade applied tag"
                >
                  Upgrade Applied
                </p>
              }
              @if (!upgradeApplied && promoPrice && price && !freeUpgrade) {
                <p
                  class="text-color-inherit PromoText text-right font-bold"
                  i18n="Project upgrade promo price"
                >
                  ONLY
                  {{ promoPrice | flCurrency: currency.code }}
                </p>
                <p
                  class="UpgradeItem-price-full text-right font-bold"
                  i18n="Project upgrade promo price"
                >
                  {{ price | flCurrency: currency?.code }}
                </p>
              }
            }
            @if (isSoldOut) {
              <p
                class="text-right font-bold"
                i18n="Project upgrade sold out"
              >
                SOLD OUT
              </p>
            }
          </div>
          @if (tooltip) {
            <fl-icon
              [matTooltip]="tooltip"
              [matTooltipPosition]="'above'"
              [name]="'ui-info'"
              [size]="IconSize.SMALL"
              [useIconFont]="true"
            >
            </fl-icon>
          }
        </div>
      } @else {
        <fl-loading-text [rows]="1"></fl-loading-text>
      }
    </ng-template>
  `,
  styleUrls: ['./upgrade-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpgradeItemComponent {
  IconSize = IconSize;
  VerticalAlignment = VerticalAlignment;

  @Input() currency: Currency;
  @Input() freeUpgrade: boolean;
  /** Renders the price crossed out with an "Included" tag underneath. */
  @Input() includedUpgrade: boolean;
  @Input() isLoadingPrice: boolean;
  @Input() isSoldOut: boolean;
  @Input() price: number;
  @Input() promoPrice: number;
  @Input() upgradeApplied: boolean;
  @Input() upgradeType: UpgradeType | BidUpgradeType | EntryUpgradeType;
  @Input() tooltip: string;

  @Input()
  variation: 'default' | 'compact' = 'default';
}
